import React, { useEffect } from 'react';
import Navbar from '../components/Navbar';
import { motion } from 'framer-motion';
import { FaTwitter, FaInstagram, FaBehance, FaArrowRight, FaCheckCircle, FaRocket, FaLightbulb, FaUsers, FaBullseye } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import backgroundImage from '../images/about.jpg';

const AboutUs = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-perspective-carbon text-perspective-sand relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute inset-0 bg-grid-soft [background-size:28px_28px] opacity-20" />
      </div>

      <div className="relative z-10">
        <Navbar />
        <div className="h-16" />
        
        {/* Hero Section */}
        <section className="px-4 pt-12 pb-12 lg:pt-16 lg:pb-16">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-sm font-semibold text-perspective-sage uppercase tracking-[0.08em] mb-4">Who we are</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                About Perspective HQ
              </h1>
              <p className="text-lg sm:text-xl text-perspective-cloud/85 max-w-3xl mx-auto leading-relaxed">
                We're a team of passionate designers, developers, and strategists dedicated to creating digital experiences that drive real business results.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Company Overview */}
        <section className="px-4 py-16 lg:py-20">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <p className="text-sm font-semibold text-perspective-sage uppercase tracking-[0.08em] mb-4">Our story</p>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                  Building Digital Excellence
                </h2>
                <p className="text-lg text-perspective-cloud/85 mb-6 leading-relaxed">
                  At Perspective HQ, we craft websites, apps, and digital solutions that drive business growth. We focus on delivering user-centered, innovative experiences that help businesses thrive in the digital world.
                </p>
                <p className="text-lg text-perspective-cloud/85 leading-relaxed">
                  Our team works closely with you to bring your vision to life, ensuring your digital presence is as impactful as your business.
                </p>
              </motion.div>
              
              <motion.div
                className="relative"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-elevated">
                  <img 
                    src={backgroundImage} 
                    alt="Our team at work" 
                    className="w-full h-96 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-perspective-carbon/60 to-transparent" />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Our Approach */}
        <section className="px-4 py-16 lg:py-20 bg-perspective-carbon">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-sm font-semibold text-perspective-sage uppercase tracking-[0.08em] mb-4">How we work</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Our Approach</h2>
              <p className="text-lg text-perspective-cloud/80 max-w-2xl mx-auto">
                We combine strategy, design, and technology to deliver tailored solutions. Our process involves understanding your goals, creating effective designs, and launching seamless products.
              </p>
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[ 
                {
                  icon: FaBullseye,
                  title: "Discovery & Strategy",
                  content: "We start by understanding your business needs and defining the roadmap."
                },
                {
                  icon: FaLightbulb,
                  title: "Design & Development",
                  content: "We create functional, visually appealing solutions tailored to your brand."
                },
                {
                  icon: FaRocket,
                  title: "Testing & Launch",
                  content: "We rigorously test your product and launch it with precision."
                },
                {
                  icon: FaUsers,
                  title: "Ongoing Support",
                  content: "We continue to support you post-launch to ensure long-term success."
                }
              ].map((step, index) => (
                <motion.div
                  key={index}
                  className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 hover:border-perspective-sage/30 transition-all duration-300"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-center mb-4 text-perspective-sage">
                    <step.icon className="w-6 h-6" />
                    <h4 className="text-lg font-semibold ml-3 text-white">{step.title}</h4>
                  </div>
                  <p className="text-perspective-cloud/80 text-sm">{step.content}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section className="px-4 py-16 lg:py-20 bg-perspective-ink/60 backdrop-blur">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-sm font-semibold text-perspective-sage uppercase tracking-[0.08em] mb-4">What drives us</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Our Values</h2>
              <p className="text-lg text-perspective-cloud/80 max-w-2xl mx-auto">
                The principles that guide everything we do and shape our relationships with clients.
              </p>
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[ 
                { title: "Customer Focus", content: "We prioritize your needs and deliver exceptional experiences." },
                { title: "Innovation", content: "We push boundaries to create modern solutions." },
                { title: "Collaboration", content: "We believe in working together to achieve mutual success." },
                { title: "Integrity", content: "We operate with transparency, honesty, and accountability." },
              ].map((value, index) => (
                <motion.div
                  key={index}
                  className="bg-white/5 backdrop-blur border border-white/10 p-6 rounded-2xl hover:border-perspective-sage/30 transition-all duration-300"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-start space-x-4">
                    <FaCheckCircle className="w-5 h-5 mt-1 flex-shrink-0 text-perspective-sage" />
                    <div>
                      <h4 className="text-xl font-semibold mb-2 text-white">{value.title}</h4>
                      <p className="text-perspective-cloud/80">{value.content}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="px-4 py-16 lg:py-20">
          <div className="max-w-6xl mx-auto rounded-3xl overflow-hidden bg-gradient-to-r from-perspective-carbon to-perspective-border text-white shadow-elevated border border-perspective-sage/15">
            <div className="p-10 md:p-12 text-center">
              <p className="text-sm uppercase tracking-[0.12em] text-perspective-mint mb-4">Why work with us</p>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">Why Choose Us</h2>
              <p className="text-lg text-perspective-mint/90 mb-8 max-w-2xl mx-auto">
                Perspective HQ is more than just an agency — we're a team of innovators, designers, and strategists who care about your success. We combine creativity with technology to deliver powerful digital solutions that drive results.
              </p>
              <button 
                onClick={() => navigate('/contact')}
                className="group inline-flex items-center gap-2 bg-perspective-sage text-perspective-carbon px-8 py-4 rounded-full font-semibold hover:scale-105 transition-all duration-300 shadow-lg"
              >
                Get in Touch
                <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-perspective-carbon text-white border-t border-white/10">
          <div className="max-w-6xl mx-auto px-4 py-14">
            <div className="grid md:grid-cols-4 gap-8 mb-10">
              <div className="md:col-span-2">
                <img 
                  src="/perspectivelogo.png" 
                  alt="Perspective HQ" 
                  className="h-8 w-auto mb-4"
                />
                <p className="text-perspective-mint/90 max-w-xl mb-6">
                  Creating digital experiences that drive growth and innovation. We're passionate about building solutions that make a difference.
                </p>
                <div className="flex gap-3">
                  <a href="https://twitter.com/perspectivehq" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-perspective-sage hover:text-perspective-carbon transition-colors">
                    <FaTwitter size={16} />
                  </a>
                  <a href="https://instagram.com/theperspectivehq" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-perspective-sage hover:text-perspective-carbon transition-colors">
                    <FaInstagram size={16} />
                  </a>
                  <a href="https://www.behance.net/skaina" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-perspective-sage hover:text-perspective-carbon transition-colors">
                    <FaBehance size={16} />
                  </a>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-4">Navigate</h4>
                <div className="space-y-2 text-perspective-mint/90">
                  <button onClick={() => navigate('/work')} className="block hover:text-perspective-sage transition-colors">Work</button>
                  <button onClick={() => navigate('/services')} className="block hover:text-perspective-sage transition-colors">Services</button>
                  <button onClick={() => navigate('/about')} className="block hover:text-perspective-sage transition-colors">About</button>
                  <button onClick={() => navigate('/blog')} className="block hover:text-perspective-sage transition-colors">Blog</button>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-4">Contact</h4>
                <div className="space-y-2 text-perspective-mint/90">
                  <p>info@perspectivehq.co.za</p>
                  <p>+27 69 339 5619</p>
                  <p>Durban, South Africa</p>
                </div>
              </div>
            </div>
            <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-sm text-perspective-mint/80">
              <p>© 2021-2025 Perspective HQ. All rights reserved.</p>
              <div className="flex gap-4">
                <button onClick={() => navigate('/privacy-policy')} className="hover:text-perspective-sage transition-colors">Privacy Policy</button>
                <button onClick={() => navigate('/terms-of-service')} className="hover:text-perspective-sage transition-colors">Terms of Service</button>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default AboutUs;
